'use client'
import { useEffect, useRef, useState } from 'react';

export default function GujaratiInput({
    value,
    onChange,
    language,
    placeholder,
    className,
    type = 'text',
    id,
    name,
    required,
    rows,
    as
}) {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);
    const isTextarea = type === 'textarea' || as === 'textarea';

    useEffect(() => {
        if (value !== undefined && value !== inputValue) {
            setInputValue(value);
        }
    }, [value]);

    // Google Transliterate API call
    const transliterateWithGoogle = async (text) => {
        if (!text || language !== 'gujarati') return [];

        try {
            const url = `https://inputtools.google.com/request?text=${encodeURIComponent(text)}&itc=gu-t-i0-und&num=5&cp=0&cs=1&ie=utf-8&oe=utf-8&app=demopage`;

            const response = await fetch(url);
            const data = await response.json();

            if (data && data[1] && data[1][0] && data[1][0][1]) {
                return data[1][0][1]; // Returns array of suggestions
            }
            return [];
        } catch (error) {
            console.error('Transliteration error:', error);
            return [];
        }
    };

    // Get the last word being typed
    const getLastWord = (text, cursorPosition) => {
        const beforeCursor = text.substring(0, cursorPosition);
        const words = beforeCursor.split(/\s/);
        return words[words.length - 1];
    };

    const handleChange = async (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        if (language === 'gujarati') {
            const cursorPosition = e.target.selectionStart;
            const lastWord = getLastWord(newValue, cursorPosition);

            if (lastWord.length > 0 && /^[a-zA-Z]+$/.test(lastWord)) {
                // Get suggestions from Google
                const googleSuggestions = await transliterateWithGoogle(lastWord);
                if (googleSuggestions.length > 0) {
                    setSuggestions(googleSuggestions);
                    setShowSuggestions(true);
                    setSelectedIndex(0);
                } else {
                    setShowSuggestions(false);
                }
            } else {
                setShowSuggestions(false);
            }

            onChange({ target: { name, value: newValue } });
        } else {
            setInputValue(newValue);
            onChange({ target: { name, value: newValue } });
            setShowSuggestions(false);
        }
    };

    const applySuggestion = (suggestion) => {
        const cursorPosition = inputRef.current.selectionStart;
        const text = inputValue;
        const beforeCursor = text.substring(0, cursorPosition);
        const afterCursor = text.substring(cursorPosition);

        const words = beforeCursor.split(/\s/);
        words[words.length - 1] = suggestion;

        const newText = words.join(' ') + afterCursor;
        setInputValue(newText);
        onChange({ target: { name, value: newText } });
        setShowSuggestions(false);

        // Set focus back to input
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                break;
            case 'Enter':
            case 'Tab':
                if (showSuggestions && suggestions[selectedIndex]) {
                    e.preventDefault();
                    applySuggestion(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                break;
        }
    };

    const commonProps = {
        ref: inputRef,
        id,
        name,
        value: inputValue,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        placeholder,
        className,
        required,
        autoComplete: 'off'
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {isTextarea ? (
                <textarea {...commonProps} rows={rows || 4} />
            ) : (
                <input {...commonProps} type={type} />
            )}

            {showSuggestions && suggestions.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '2px'
                }}>
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            onClick={() => applySuggestion(suggestion)}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                backgroundColor: index === selectedIndex ? '#e3f2fd' : 'white',
                                borderBottom: index < suggestions.length - 1 ? '1px solid #eee' : 'none',
                                fontSize: '16px',
                                fontFamily: 'inherit'
                            }}
                            onMouseEnter={() => setSelectedIndex(index)}
                        >
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
